import { ImgID } from "../../../image_loader/img_id.js";
import { OfferService } from "../../../offers/offer_service.js";
import { isPurchaseErrorReported } from "../../../offers/purchase_failure.js";
import { crashCapture } from "../../../crash/crash_reporter.js";
import { offerButtonLabel, offerLocalizedText } from "../../../offers/offer_visual_registry.js";
import { showRewarded } from "../../../ads/rewarded.js";
import { formatMinutesShort } from "../../../common/reward_format.js";
import type { ClaimFreeOfferResponse, OfferDto, OfferRewardDto, ValidatePurchaseResponse } from "../../../api/dto/offers.dto.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../../api/player_sync.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, UITextOptions } from "../../common_ui_elements/ui_text_element.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";

export const CONTINUE_SPECIAL_OFFER_PLACEMENT = "continue_dialog";
export const CONTINUE_SPECIAL_OFFER_TEMPLATE = "continue_special_offer";

export interface ContinueSpecialOfferPanelParams
{
  offer: OfferDto;
  /** Decision that opened this placement; absent when it came from the catalog. */
  trigger_event_id?: string;
  /** Called with the granted level shots (0 when the bundle carries none). */
  on_purchased: ( level_shots: number ) => void;
  /** Placement the offer was resolved for; the regular Continue dialog by default. */
  placement?: string;
  /** Fallback for the line under the title, with the shots as {0}; the level's wording by default. */
  description_key?: string;
  /** Display only (no server offer behind it yet): no analytics, no price lookup, the button buys nothing. */
  preview?: boolean;
}

/**
 * The "special offer" strip under the Continue dialog: title tab, one line of
 * copy, the reward row and the price button. Everything but the layout comes
 * from the placement offer — texts, rewards, purchase mode, price.
 */
export class ContinueSpecialOfferPanel extends UIElementHolder
{
  public static readonly DESIGN_W = 1018;
  public static readonly DESIGN_H = 442;
  private static readonly FREE_BUTTON_HUE_ROTATION = 160;

  private static readonly TEXT_FONT_SIZE     = 50;
  private static readonly TEXT_OUTLINE_W     = 14;
  private static readonly TEXT_OUTLINE_COLOR = "#103A92";

  private static readonly STRIP_W = 882;
  private static readonly STRIP_H = 193;
  private static readonly STRIP_Y = 203;
  private static readonly STRIP_ART_INSET_X = 9;

  private static readonly BUTTON_W       = 319;
  private static readonly BUTTON_TEXT_OUTLINE_W = 8;
  private static readonly BUTTON_TEXT_OUTLINE_OFFSET_Y = 2;
  private static readonly BUTTON_H       = 181;
  private static readonly BUTTON_Y       = 6;
  private static readonly BUTTON_INSET_X = 10;
  private static readonly GREEN_BUTTON_ART_INSET = 10 / 340;
  private static readonly VIDEO_BUTTON_ART_INSET = 10 / 435;

  private static readonly REWARD_ICON_SIZE  = 120;
  private static readonly REWARD_PLUS_W     = 40;
  private static readonly REWARD_CENTER_Y   = 80;
  private static readonly REWARD_NUDGE_Y    = 5;
  private static readonly REWARD_AREA_PAD_X = 24;
  private static readonly REWARD_FONT_SIZE  = 38;

  private price_text: UITextElement | null = null;
  private purchase_in_progress = false;
  private purchased = false;
  private close_tracked = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    private readonly params: ContinueSpecialOfferPanelParams
  )
  {
    super( ctx, x, y, LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.DESIGN_W ), LayoutScaler.heightFromDesign( ContinueSpecialOfferPanel.DESIGN_H ) );
    this.createBackground();
    this.createTitleBadge();
    this.createDescription();
    this.createRewardsAndButton();
    void this.loadPrice();
    if ( params.preview ) return;
    // The panel is both the entry point becoming visible and the offer being
    // shown, so it reports the same pair the lobby button + dialog do.
    void OfferService.trackPlacementImpression( params.offer.id, this.analyticsContext() );
    void OfferService.trackShown( params.offer.id, this.analyticsContext() );
  }

  /** Reported once by the overlay when it goes away without this offer being bought. */
  public trackClosed( reason: string ): void
  {
    if ( this.purchased || this.close_tracked || this.params.preview ) return;
    this.close_tracked = true;
    void OfferService.trackClosed( this.params.offer.id, this.analyticsContext( { reason } ) );
  }

  public name(): string { return "ContinueSpecialOfferPanel"; }

  /** A reused Continue overlay keeps a preview panel: it reports nothing and buys nothing. */
  public isPreviewOf( offer: OfferDto ): boolean
  {
    return this.params.preview === true && this.params.offer === offer;
  }

  private analyticsContext( extra: Record<string, unknown> = {} ): Record<string, unknown>
  {
    return {
      placement: this.params.placement ?? CONTINUE_SPECIAL_OFFER_PLACEMENT,
      placement_id: this.params.placement ?? CONTINUE_SPECIAL_OFFER_PLACEMENT,
      template: CONTINUE_SPECIAL_OFFER_TEMPLATE,
      ...( this.params.trigger_event_id ? { trigger_event_id: this.params.trigger_event_id } : {} ),
      ...extra,
    };
  }

  private static textOptions( design_font_size: number = ContinueSpecialOfferPanel.TEXT_FONT_SIZE, scale = 1 ): UITextOptions
  {
    return {
      font_size:      LayoutScaler.textSizeFromDesign( design_font_size * scale ),
      font_weight:    900,
      text_color:     ColorScheme.white,
      text_style:     TextStyleScheme.geologica,
      text_align:     MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width:  LayoutScaler.textBorderSizeFromDesign( ContinueSpecialOfferPanel.TEXT_OUTLINE_W * scale ),
      outline_color:  ContinueSpecialOfferPanel.TEXT_OUTLINE_COLOR,
      auto_wrap:      false,
      scale_to_width: true,
    };
  }

  private createBackground(): void
  {
    this.addChild( new UINineSliceElement(
      this.ctx, 0, 0, this.rect.getW(), this.rect.getH(), ImgID.UI_CapsuleBlue, "capsule_bg"
    ) );
  }

  private createTitleBadge(): void
  {
    const badge_w = LayoutScaler.widthFromDesign( 668 );
    const badge_h = LayoutScaler.heightFromDesign( 162 );
    const badge_x = ( this.rect.getW() - badge_w ) / 2;
    const badge_y = LayoutScaler.yFromDesign( -22 );
    const badge = new UIImage( this.ctx, badge_x, badge_y, badge_w, badge_h, ImgID.UI_ContinueOfferTitleBadge );
    this.addChild( badge );

    const text_w = badge_w * 0.8;
    badge.addChild( new UITextElement(
      this.ctx, ( badge_w - text_w ) / 2, LayoutScaler.yFromDesign( 30 ), text_w, LayoutScaler.heightFromDesign( 69 ),
      {
        text: offerLocalizedText( this.params.offer.texts?.title_key, "OFFER.CONTINUE.TITLE", "Special offer" ),
        ...ContinueSpecialOfferPanel.textOptions(),
      }
    ) );
  }

  private createDescription(): void
  {
    const shots = this.params.offer.rewards?.level_shots ?? 0;
    const description_key = this.params.offer.texts?.description_key;
    const text_w = this.rect.getW() * 0.8;
    this.addChild( new UITextElement(
      this.ctx, ( this.rect.getW() - text_w ) / 2, LayoutScaler.yFromDesign( 129 ), text_w, LayoutScaler.heightFromDesign( 69 ),
      {
        text: offerLocalizedText( description_key, this.params.description_key ?? "OFFER.CONTINUE.PLUS_SHOTS", "+{0} shots for this level", [ String( shots ) ] ),
        ...ContinueSpecialOfferPanel.textOptions(),
      }
    ) );
  }

  private getRewards(): OfferRewardDto[]
  {
    const raw = this.params.offer.rewards ?? {};
    return Object.entries( raw ).flatMap( ( [ type, amount ] ) =>
    {
      if ( type === 'no_ads' && amount === true ) return [ { type: 'no_ads' as const, amount: 1 } ];
      return typeof amount === 'number' && amount > 0 ? [ { type: type as OfferRewardDto['type'], amount } ] : [];
    } );
  }

  private createRewardsAndButton(): void
  {
    // Dark strip with the reward icons on the left and the price on the right.
    const strip_w = LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.STRIP_W );
    const strip_h = LayoutScaler.heightFromDesign( ContinueSpecialOfferPanel.STRIP_H );
    const strip = new UINineSliceElement(
      this.ctx,
      ( this.rect.getW() - strip_w ) / 2,
      LayoutScaler.yFromDesign( ContinueSpecialOfferPanel.STRIP_Y ),
      strip_w,
      strip_h,
      ImgID.UI_TowerFailDarkBlueSubstrate,
      "tower_fail_dark_blue_substrate"
    );
    this.addChild( strip );

    const button_left = this.createButton( strip, strip_w, strip_h );
    this.createRewards( strip, LayoutScaler.cornerRadiusFromDesign( ContinueSpecialOfferPanel.STRIP_ART_INSET_X ), button_left );
  }

  private createButton( strip: UINineSliceElement, strip_w: number, strip_h: number ): number
  {
    const offer = this.params.offer;
    const rewarded = OfferService.isRewardedOffer( offer.id );
    const button_w = LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.BUTTON_W );
    const button_h = rewarded ? button_w * ( 133 / 435 ) : LayoutScaler.heightFromDesign( ContinueSpecialOfferPanel.BUTTON_H );
    const button_x = strip_w - button_w - LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.BUTTON_INSET_X );
    const button_y = rewarded ? ( strip_h - button_h ) / 2 : LayoutScaler.yFromDesign( ContinueSpecialOfferPanel.BUTTON_Y );
    const button = new UIImage(
      this.ctx, button_x, button_y, button_w, button_h,
      rewarded ? ImgID.UI_ChainOfferVideoIcon : ImgID.UI_TowerFailGreenBtnHalf, {}, true
    );
    if ( !rewarded && OfferService.isFreeOffer( offer.id ) ) button.setHueRotation( ContinueSpecialOfferPanel.FREE_BUTTON_HUE_ROTATION );
    strip.addChild( button );

    const label_x = rewarded ? button_w * 0.22 : button_w * 0.1;
    const label_w = rewarded ? button_w - label_x : button_w * 0.8;
    this.price_text = new UITextElement( this.ctx, label_x, 0, label_w, button_h, {
      text: "…",
      font_size: LayoutScaler.textSizeFromDesign( rewarded ? 40 : ContinueSpecialOfferPanel.TEXT_FONT_SIZE ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      outline_color: rewarded ? ColorScheme.more_lives_purple_outline : ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( ContinueSpecialOfferPanel.BUTTON_TEXT_OUTLINE_W ),
      outline_offset_y: LayoutScaler.yFromDesign( ContinueSpecialOfferPanel.BUTTON_TEXT_OUTLINE_OFFSET_Y ),
      ...TextStyleScheme.buttonShadow(),
    } );
    button.addChild( this.price_text, true );
    button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      void this.purchase();
    } );
    const art_inset = rewarded ? ContinueSpecialOfferPanel.VIDEO_BUTTON_ART_INSET : ContinueSpecialOfferPanel.GREEN_BUTTON_ART_INSET;
    return button_x + button_w * art_inset;
  }

  private createRewards( strip: UINineSliceElement, area_left: number, area_right: number ): void
  {
    const all = this.getRewards();
    const rewards = [
      ...all.filter( reward => reward.type === 'level_shots' ),
      ...all.filter( reward => reward.type !== 'level_shots' ),
    ].slice( 0, 4 );
    if ( !rewards.length ) return;

    const natural_icon_w = LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.REWARD_ICON_SIZE );
    const natural_plus_w = LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.REWARD_PLUS_W );
    const natural_row_w  = rewards.length * natural_icon_w + ( rewards.length - 1 ) * natural_plus_w;
    const area_w         = area_right - area_left;
    const available_w    = area_w - LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.REWARD_AREA_PAD_X ) * 2;
    const scale = Math.min( 1, available_w / natural_row_w );

    const icon_w  = natural_icon_w * scale;
    const icon_h  = LayoutScaler.heightFromDesign( ContinueSpecialOfferPanel.REWARD_ICON_SIZE ) * scale;
    const plus_w  = natural_plus_w * scale;
    const row_x   = area_left + ( area_w - natural_row_w * scale ) / 2;
    const icon_y  = LayoutScaler.yFromDesign( ContinueSpecialOfferPanel.REWARD_CENTER_Y )
      + ContinueSpecialOfferPanel.REWARD_NUDGE_Y * LayoutScaler.getScale() - icon_h / 2;
    const slop    = LayoutScaler.widthFromDesign( 20 ) * scale;
    const label_h = LayoutScaler.heightFromDesign( 60 ) * scale;
    const label_y = icon_y + icon_h - label_h + LayoutScaler.yFromDesign( 10 ) * scale;
    const text_options = ContinueSpecialOfferPanel.textOptions( ContinueSpecialOfferPanel.REWARD_FONT_SIZE, scale );

    rewards.forEach( ( reward, index ) =>
    {
      const x = row_x + index * ( icon_w + plus_w );
      const icon = new UIImage( this.ctx, x, icon_y, icon_w, icon_h, ContinueSpecialOfferPanel.rewardIcon( reward.type ) );
      strip.addChild( icon );
      const label = new UITextElement( this.ctx, x - slop, label_y, icon_w + slop * 2, label_h, {
        text: ContinueSpecialOfferPanel.rewardLabel( reward ),
        ...text_options,
      } );
      strip.addChild( label );
      RewardTooltip.attach( icon, reward.type, [ label ] );
      if ( index < rewards.length - 1 )
        strip.addChild( new UITextElement( this.ctx, x + icon_w, icon_y, plus_w, icon_h, { text: "+", ...text_options } ) );
    } );
  }

  private static rewardIcon( type: OfferRewardDto['type'] ): ImgID
  {
    switch ( type )
    {
      case 'coins': return ImgID.UI_ShopBlockImg;
      case 'hearts': return ImgID.IconHeart;
      case 'infinite_hearts_minutes': return ImgID.UI_ShopInfiniteHeart;
      case 'big_balls': return ImgID.UI_GameStartBigBallBooster;
      case 'infinite_balls': return ImgID.UI_GameStartInfiniteBallsBooster;
      case 'multi_balls': return ImgID.UI_GameStartMultiballsBooster;
      case 'fireworks': return ImgID.UI_GameStartFireworkBooster;
      case 'mines': return ImgID.UI_PassiveBoosterBomb;
      case 'level_shots': return ImgID.UI_DailyTasksTaskCannon;
      case 'no_ads': case 'no_ads_minutes': return ImgID.UI_RemoveAdsIcon;
    }
  }

  private static rewardLabel( reward: OfferRewardDto ): string
  {
    if ( reward.type === 'level_shots' ) return `+${reward.amount}`;
    if ( reward.type === 'infinite_hearts_minutes' || reward.type === 'no_ads_minutes' ) return formatMinutesShort( reward.amount );
    if ( reward.type === 'no_ads' ) return 'NO ADS';
    if ( reward.type === 'coins' ) return String( reward.amount );
    return `x${reward.amount}`;
  }

  private setPrice( text: string ): void
  {
    this.price_text?.setText( text );
    this.invalidateSubtree();
  }

  private async loadPrice(): Promise<void>
  {
    const offer = this.params.offer;
    if ( this.params.preview )
    {
      this.setPrice( offerButtonLabel( offer, "BUY" ) );
      return;
    }
    if ( OfferService.isFreeOffer( offer.id ) || OfferService.isRewardedOffer( offer.id ) )
    {
      this.setPrice( offerButtonLabel( offer, "FREE" ) );
      return;
    }
    const product_id = OfferService.getPlatformProductId( offer.id );
    if ( !product_id ) { this.setPrice( "N/A" ); return; }
    try
    {
      const details = await ( await import( "../../../plugins/iap_plugin.js" ) ).IapPlugin.getProductDetails( product_id );
      this.setPrice( details?.formatted_price ?? offerButtonLabel( offer, "BUY" ) );
      const price_context = this.analyticsContext( {
        productid: product_id, formatted_price: details?.formatted_price, currency: details?.currency, price_micros: details?.price_micros,
      } );
      if ( details?.formatted_price ) await OfferService.trackPriceLoaded( offer.id, price_context );
      else await OfferService.trackPriceUnavailable( offer.id, { ...price_context, failure_reason: 'product_details_missing' } );
    }
    catch
    {
      this.setPrice( offerButtonLabel( offer, "BUY" ) );
      await OfferService.trackPriceUnavailable( offer.id, this.analyticsContext( { productid: product_id } ) );
    }
  }

  private async purchase(): Promise<void>
  {
    if ( this.purchase_in_progress ) return;
    if ( this.params.preview )
    {
      console.info( `[ContinueOffer] ${this.params.offer.id} is a preview, nothing to buy until the server has an offer on ${this.params.placement}` );
      return;
    }
    this.purchase_in_progress = true;
    const offer = this.params.offer;
    const previous = this.price_text?.getText() ?? "";
    this.setPrice( "..." );
    try
    {
      await OfferService.trackClicked( offer.id, this.analyticsContext() );
      let result: ValidatePurchaseResponse | ClaimFreeOfferResponse;
      if ( OfferService.isRewardedOffer( offer.id ) )
      {
        const watched = await showRewarded( 'continue_offer' );
        await OfferService.trackRewardedAdOutcome( offer.id, watched, this.analyticsContext() );
        if ( !watched.rewarded ) { this.setPrice( previous ); return; }
        result = await OfferService.claimFreeOffer( offer.id, this.analyticsContext( { rewarded: true, rewarded_ad_id: watched.ad_id } ), { ad_id: watched.ad_id } );
      }
      else if ( OfferService.isFreeOffer( offer.id ) )
        result = await OfferService.claimFreeOffer( offer.id, this.analyticsContext() );
      else
        result = await OfferService.purchaseOffer( offer.id, this.analyticsContext() );

      if ( result.player ) adoptEconomyFromPlayer( result.player );
      else await refreshPlayerFromServer();
      this.purchased = true;
      // Shots are not inventory: whatever the server echoed goes straight into
      // the attempt the player is about to resume.
      this.params.on_purchased( result.granted?.level_shots ?? 0 );
    }
    catch ( error )
    {
      console.warn( '[ContinueOffer] purchase failed', error );
      if ( !isPurchaseErrorReported( error ) )
        crashCapture( error, { phase: 'continue_offer_purchase' } );
      this.setPrice( previous );
    }
    finally
    {
      this.purchase_in_progress = false;
    }
  }
}
